<template>
  <a-spin :spinning="spinning">
    <div :style="outerCardStyle">
      <div class="outer-title-bar">
        <span style="display: flex; align-items: center;">
          <span :style="pieCardIconStyle">
            <component :is="TeamOutlined" />
          </span>
          <span class="pies-and-table-card-title">统一互联 PMC</span>
        </span>
      </div>
      <a-row :gutter="16" justify="center">
        <a-col v-for="(stat, index) in stats" :key="index" :span="8">
          <div :style="statStyle">
            <div :style="iconStyle">
              <component :is="stat.icon" />
            </div>
            <div class="stat-content">
              <div class="stat-title">{{ stat.title }}</div>
              <div class="stat-value-row">
                <div class="stat-value">{{ formatNumber(stat.value) }}</div>
                <span class="stat-suffix">{{ stat.suffix }}</span>
              </div>
            </div>
          </div>
        </a-col>
      </a-row>

      <div :style="pieCardStyle">
        <div class="pie-and-table-cards-flex">
          <div class="pie-and-table-card">
            <div class="pie-title-container">
              <div class="pie-title">华为 vs 非华为</div>
            </div>
            <div style="height: 100%;">
              <div style="height: 200px; margin-bottom: 12px;">
                <my-pie-chart :data="leftPieData.contributor" :colors="huaweiNonHuaweiColors" suffix="人" />
              </div>
              <div style="display: flex;">
                <div v-for="(data, index) in leftPieData.contributor" :key="index" class="pie-data-box"
                  :style="{ borderColor: huaweiNonHuaweiColors[index] }">
                  <div class="pie-data-title" style="margin-bottom: 6px;">
                    <span class="pie-data-dot" :style="{ color: huaweiNonHuaweiColors[index] }">●</span>
                    {{ data.name }}
                  </div>
                  <span style="font-size: 16px; font-weight: 600;">
                    {{ formatNumber(data.value) }}
                  </span>
                  <span class="pie-data-suffix"> 人</span>
                  <div style="font-size: 12px; color: #666; margin-top: 5px;">
                    占比 {{((data.value / leftPieData.contributor.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(2)}}%
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="pie-and-table-card" style="min-width: 60%;">
            <div class="pie-title-container">
              <div class="pie-title">非华为构成</div>
            </div>
            <div style="display: flex; align-items: flex-start;">
              <div style="width: 320px;">
                <my-pie-chart :data="rightPieData.contributor" :colors="rightPieColors" suffix="人" />
              </div>
              <div style="flex: 1; display: flex; flex-direction: column; gap: 6px; padding-right: 8px;">
                <div v-for="(data, index) in displayedRightPieData" :key="index" class="pie-data-box"
                  :style="getRightPieItemStyle(data)">
                  <div class="pie-data-title">
                    <span class="pie-data-dot" :style="{ color: getNonHuaweiColor(data.name) }">●</span>
                    {{ data.name }}
                  </div>
                  <div style="text-align: right;">
                    <span style="font-size: 14px; font-weight: 600;">
                      {{ formatNumber(data.value) }}
                    </span>
                    <span class="pie-data-suffix"> 人</span>
                    <div style="font-size: 12px; margin-top: 2px;">
                      占非华为 {{ ((data.value / rightPieTotalSum) * 100).toFixed(2) }}% | 占总体 {{ ((data.value / leftPieTotalSum) * 100).toFixed(2) }}%
                    </div>
                  </div>
                </div>
                <div class="pie-data-box pie-data-total-box">
                  <div class="pie-data-title">
                    <span class="pie-data-dot" style="color: #6366F1; font-size: 18px; margin-right: 2px;">∑</span>
                    <span style="font-weight: 700;">合计</span>
                  </div>
                  <div style="text-align: right;">
                    <span style="font-size: 16px; font-weight: 600;">
                      {{ formatNumber(rightPieDataSum) }}
                    </span>
                    <span class="pie-data-suffix"> 人</span>
                    <div style="font-size: 12px; margin-top: 2px;">
                      占非华为 {{ rightPieTotalSum ? ((rightPieDataSum / rightPieTotalSum) * 100).toFixed(2) : '0.00' }}% | 占总体 {{ leftPieTotalSum ? ((rightPieDataSum / leftPieTotalSum) * 100).toFixed(2) : '0.00' }}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </a-spin>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { TeamOutlined, CodeOutlined, EnvironmentOutlined } from '@ant-design/icons-vue';
import MyPieChart from '../components/MyPieChart.vue';

const THEME_COLOR = '#13C2C2';

const nonHuaweiOrder = ['HABO', 'DONATE', 'PARTNER', 'SCHOOL', 'OTHERS'];
const nonHuaweiNameMap = { HABO: '伙伴：哈勃', DONATE: '伙伴：捐赠单位', PARTNER: '伙伴：其他', SCHOOL: '高校', OTHERS: '其他' };
const huaweiNonHuaweiColors = ['#EF4444', '#3B82F6'];
const nonHuaweiColorMap = {
  '伙伴：哈勃': '#15803D',
  '伙伴：捐赠单位': '#16A34A',
  '伙伴：其他': '#22C55E',
  '高校': '#60A5FA',
  '其他': '#A78BFA'
};

const formatNumber = (n) => {
  if (n === null || n === undefined) return '';
  const num = typeof n === 'number' ? n : Number(n);
  return Number.isNaN(num) ? n : num.toLocaleString();
};

const buildNonHuaweiContributor = (situations) => {
  const contributorArr = [];
  for (const key of nonHuaweiOrder) {
    const item = situations.find((s) => s.remarks === key);
    if (item) contributorArr.push({ name: nonHuaweiNameMap[key], value: item.number });
  }
  return contributorArr;
};

const getNonHuaweiColor = (name) => nonHuaweiColorMap[name] || '#A78BFA';

const spinning = ref(false);
const totalNum = ref(1248);
const totalCodeLines = ref(8456890);
const organizationNum = ref(42);

const stats = computed(() => [{
  title: '贡献者数量',
  value: totalNum.value,
  suffix: '人',
  icon: TeamOutlined
}, {
  title: '代码量',
  value: totalCodeLines.value,
  suffix: '行',
  icon: CodeOutlined
}, {
  title: '组织数量',
  value: organizationNum.value,
  suffix: '家',
  icon: EnvironmentOutlined
}]);

const leftPieData = computed(() => ({
  contributor: [
    { name: '华为', value: 846 },
    { name: '非华为', value: 402 }
  ]
}));

const rightPieData = computed(() => ({
  contributor: buildNonHuaweiContributor([
    { remarks: 'PARTNER', number: 169 },
    { remarks: 'HABO', number: 113 },
    { remarks: 'DONATE', number: 80 },
    { remarks: 'OTHERS', number: 40 }
  ])
}));

const rightPieColors = computed(() => (rightPieData.value.contributor || []).map((item) =>
  getNonHuaweiColor(item.name)));

const leftPieTotalSum = computed(() => {
  const data = leftPieData.value.contributor || [];
  return data.reduce((sum, item) => sum + item.value, 0);
});

const rightPieTotalSum = computed(() => {
  const data = rightPieData.value.contributor || [];
  return data.reduce((sum, item) => sum + item.value, 0);
});

const displayedRightPieData = computed(() => rightPieData.value.contributor || []);

const rightPieDataSum = computed(() => {
  return displayedRightPieData.value
    .reduce((sum, item) => sum + item.value, 0);
});

const getRightPieItemStyle = (item) => {
  const color = getNonHuaweiColor(item.name);
  return {
    borderColor: color,
    backgroundColor: '#FFFFFF',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '7px 16px',
    color: '#111827'
  };
};

onMounted(async () => {
});

const outerCardStyle = computed(() => ({
  border: '1px solid #E5E7EB',
  backgroundColor: '#fff',
  padding: '16px',
  borderRadius: '12px'
}));

const statStyle = computed(() => ({
  backgroundColor: '#F9FAFB',
  padding: '12px',
  borderRadius: '12px',
  minHeight: '80px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%'
}));

const iconStyle = computed(() => ({
  width: '44px',
  height: '44px',
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: THEME_COLOR + '14',
  color: THEME_COLOR,
  fontSize: '18px',
  marginRight: '12px'
}));

const pieCardIconStyle = computed(() => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: THEME_COLOR + '14',
  color: THEME_COLOR,
  fontSize: '16px',
  marginRight: '8px'
}));

const pieCardStyle = computed(() => ({
  backgroundColor: '#fff',
  padding: '16px 24px 0 24px',
  borderRadius: '12px',
  margin: '16px 0 16px 0'
}));
</script>

<style scoped>
.outer-title-bar {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 16px;
}

.stat-content {
  flex: 1;
  text-align: center;
}

.stat-title {
  color: rgba(0, 0, 0, 0.65);
  font-size: 14px;
  margin-bottom: 4px;
}

.stat-value-row {
  display: flex;
  align-items: baseline;
  justify-content: center;
}

.stat-value {
  font-size: 22px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  line-height: 1;
}

.stat-suffix {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-left: 6px;
}

.pies-and-table-card-title {
  font-size: 16px;
  font-weight: 600;
}

.pie-and-table-card {
  flex: 1;
  margin-top: 8px;
  margin-bottom: 16px;
  padding: 16px 16px 16px 16px;
  background-color: #f9fafb;
  border: 1px solid #E5E7EB;
  border-radius: 12px;
}

.pie-and-table-cards-flex {
  display: flex;
  gap: 16px;
}

.pie-data-box {
  background-color: #fff;
  border: 2px solid;
  border-radius: 12px;
  padding: 8px 12px;
  width: 100%;
  flex: 1;
  margin: 0 8px;
  background-clip: padding-box;
  align-items: center;
  text-align: left;
}

.pie-data-title {
  font-size: 14px;
  display: flex;
  align-items: baseline;
  font-weight: 500;
}

.pie-data-dot {
  font-weight: 600;
  font-family: 'MS Mincho', 'SimSun', 'serif';
  margin-right: 4px;
}

.pie-data-suffix {
  font-size: 12px;
  margin-top: 4px;
}

.pie-data-total-box {
  border-color: #95A5F5;
  background-color: #EFF6FF;
  border-width: 2px;
  color: #4338CA;
  display: flex;
  justify-content: space-between;
  padding: 5px 12px;
  box-shadow: 0 0 12px 2px #95A5F533;
}

.pie-title-container {
  margin-bottom: 8px;
  padding-left: 16px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.pie-title {
  margin-bottom: 8px;
  font-size: 16px;
  font-weight: 600;
}
</style>
